import { Link, NavLink, Outlet, useParams, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { NotificationManager } from "react-notifications";
import { API_BASE_URL, IMAGE_URL } from "./../utils/app_url";
import { useAuth } from "./../context/AuthContext";
import { useLanguage } from "./../context/LanguageContext";
import Header from "./../component/Header";
import Footer from "./../component/Footer";
import BannerSkeleton from "./BannerSkeleton";
import CircleSkeleton from "./CircleSkeleton";
import HeadingSkeleton from "./HeadingSkeleton";

import wishlistImg from "../assets/img/wishlist-icon2.svg";
import StoreHeartIcon from "./StoreHeartIcon";

const StoreLayout = () => {
    const { storeId } = useParams();
    const { user } = useAuth();
    const { selectedLanguage, langJson } = useLanguage();
    const location = useLocation();
    const navigate = useNavigate();
    const scrollPositionRef = useRef(0);

    const [store, setStore] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openLogin, setOpenLogin] = useState();

    const [isWishlisted, setIsWishlisted] = useState(false);
    const [favouriteId, setFavouriteId] = useState(null);
    const [wishlistLoading, setWishlistLoading] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");



    const handleRequestPrice = async () => {
        if (!user) {
            setOpenLogin(prev => !prev);
            return;
        }

        const res = await fetch(`${API_BASE_URL}frontend/request-price`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.authorisation.token}`,
            },
            body: JSON.stringify({
                store_id: storeId,
                lang: selectedLanguage,
            }),
        });

        const data = await res.json();
        data.res
            ? NotificationManager.success(data.msg)
            : NotificationManager.warning(data.msg);
    };

    const handleToggleStore = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!user) {
            setOpenLogin(prev => !prev);
            return;
        }

        if (wishlistLoading) return;

        // 🔥 snapshot for rollback
        const prevWishlisted = isWishlisted;
        const prevFavouriteId = favouriteId;

        // 🔥 optimistic UI update (instant)
        setWishlistLoading(true);
        setIsWishlisted(!prevWishlisted);
        if (prevWishlisted) setFavouriteId(null);

        try {
            let res;

            if (prevWishlisted && prevFavouriteId) {
                // UNFOLLOW
                res = await fetch(
                    `${API_BASE_URL}frontend/liked-stores/${prevFavouriteId}`,
                    {
                        method: "DELETE",
                        headers: {
                            Authorization: `Bearer ${user.authorisation.token}`,
                        },
                    }
                );
            } else {
                // FOLLOW
                res = await fetch(
                    `${API_BASE_URL}frontend/liked-stores`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${user.authorisation.token}`,
                        },
                        body: JSON.stringify({ store_id: storeId }),
                    }
                );
            }

            const data = await res.json();

            if (!data.res) {
                throw new Error(data.msg);
            }

            // ✅ backend truth sync
            if (!prevWishlisted) {
                setFavouriteId(data.favourite_id);
            }

            NotificationManager.success(data.msg);

            fetchStore();
        } catch (err) {
            // 🔴 rollback (instant, no reload)
            setIsWishlisted(prevWishlisted);
            setFavouriteId(prevFavouriteId);
            NotificationManager.warning(err.message || "Something went wrong");
        } finally {
            setWishlistLoading(false);
        }
    };



    const fetchStore = async () => {
        setLoading(true);
        const res = await fetch(`${API_BASE_URL}frontend/store/${storeId}`, {
            headers: {
                Authorization: `Bearer ${user?.authorisation?.token}`,
            },
        });

        const data = await res.json();

        if (data.res) {
            const storeData = data.data.store;
            setStore(storeData);
            setIsWishlisted(Boolean(storeData?.favourite_id));
            setFavouriteId(storeData?.favourite_id ?? null);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchStore();
    }, [storeId, user]);

    // Prevent scroll reset when navigating between store tabs
    useEffect(() => {
        // Continuously track scroll position
        const handleScroll = () => {
            scrollPositionRef.current = window.scrollY;
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Restore scroll position after navigation
    useEffect(() => {
        if (location.pathname.includes(`/stores/${storeId}`) && scrollPositionRef.current > 0) {
            // Multiple attempts to restore scroll (React Router can be aggressive)
            const restore = () => window.scrollTo(0, scrollPositionRef.current);

            restore(); // Immediate
            setTimeout(restore, 0); // Next tick
            setTimeout(restore, 10); // Small delay
            setTimeout(restore, 50); // Slightly longer delay
        }
    }, [location.pathname, storeId]);

    return (
        <>
            <Header openModal={openLogin} />

            {/* Banner */}
            <div className="fairDtlsBanner storeBannerNew">
                {loading ? (
                    <BannerSkeleton />
                ) : (
                    <img src={IMAGE_URL + store?.cover} alt="" />
                )}
            </div>

            {/* Store Header */}
            <div className="fullContainer">
                <div className="fairTop fairStore">
                    <div className="fairLft">
                        <div className="logoLft">
                            {loading ? (
                                <CircleSkeleton size="100px" />
                            ) : (
                                <img src={IMAGE_URL + store?.logo} alt="" />
                            )}
                        </div>
                        <div className="logoRgt">
                            {loading ? (
                                <HeadingSkeleton width="200px" height="32px" />
                            ) : (
                                <h2>{store?.name}</h2>
                            )}
                            {/* <button className="btn">{langJson?.followshop}</button>
                            <button className="btn prceBtn">{langJson?.requestforprice}</button> */}
                        </div>
                    </div>
                    <div className="fairRgtNew">
                        {/* <button className="wish" onClick={handleAddStore}>{langJson?.followshop}</button> */}
                        {!loading && (
                            <>
                                <StoreHeartIcon
                                    size={32}
                                    active={isWishlisted}
                                    disabled={wishlistLoading}
                                    onClick={handleToggleStore}
                                />

                                {/* <button className="wish wishIconNew">

                            <img
                                src={wishlistImg}
                                onClick={handleAddStore}
                                alt="wishlist"
                            />
                        </button> */}
                                <button onClick={handleRequestPrice} className="prceBtn">
                                    {langJson?.requestforprice}
                                </button>
                            </>
                        )}
                    </div>
                </div>

                <div className="storeAdvert">
                    {loading ? (
                        <div className="skeleton" style={{ height: "20px", width: "100%", marginBottom: "16px" }} />
                    ) : (
                        store?.advertisement
                    )}
                </div>

                {/* Tabs and Search Bar Container */}
                <div className="storeTabsContainer">
                    {/* Tabs (Faire-style) */}
                    <div className="storeTabs">
                        <NavLink end to={`/stores/${storeId}`}>{langJson?.allproducts}</NavLink>
                        <NavLink to={`/stores/${storeId}/bestsellers`}>{langJson?.bestsellers}</NavLink>
                        <NavLink to={`/stores/${storeId}/new`}>{langJson?.new}</NavLink>
                        <NavLink to={`/stores/${storeId}/sale`}>{langJson?.sale}</NavLink>
                        <NavLink to={`/stores/${storeId}/collections`}>{langJson?.seasons}</NavLink>
                        {/* <NavLink to={`/stores/${storeId}/categories`}>Categories</NavLink> */}
                        <NavLink to={`/stores/${storeId}/events`}>{langJson?.events}</NavLink>
                        <NavLink to={`/stores/${storeId}/about-us`}>{langJson?.aboutus}</NavLink>
                        <NavLink to={`/stores/${storeId}/videos`}>{langJson?.videos}</NavLink>
                        <NavLink to={`/stores/${storeId}/imprint-privacy-policy`}>{langJson?.imprintprivacypolicy}</NavLink>
                        <NavLink to={`/stores/${storeId}/contact-us`}>{langJson?.contactus}</NavLink>
                    </div>

                    {/* Search Bar */}
                    <div className="storeSearchBar">
                        <input
                            type="text"
                            placeholder={(langJson?.search || "Search") + " " + store?.name}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && searchTerm.trim()) {
                                    navigate(`/stores/${storeId}?search=${encodeURIComponent(searchTerm.trim())}`);
                                }
                            }}
                            className="storeSearchInput"
                        />
                    </div>
                </div>
            </div>

            {/* Dynamic Content */}
            <div style={{ minHeight: "500px" }}>
                <Outlet />
            </div>

            <Footer />
        </>
    );
};

export default StoreLayout;
